import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

interface RenameFileDialogProps {
  open: boolean;
  /** Current (full) file name, including extension. */
  currentName: string;
  isSaving?: boolean;
  onCancel: () => void;
  /** Called with the final, extension-preserved name. */
  onSave: (newName: string) => void;
}

// Splits "report.final.pdf" -> { base: "report.final", ext: ".pdf" }.
// Leading-dot files (".gitignore") and extension-less names have an empty ext.
const splitName = (name: string): { base: string; ext: string } => {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
};

export default function RenameFileDialog({
  open,
  currentName,
  isSaving = false,
  onCancel,
  onSave,
}: RenameFileDialogProps) {
  // Only the base name is editable; the extension is fixed and shown read-only.
  const { base, ext } = splitName(currentName);
  const [value, setValue] = useState(base);

  // Re-seed the input whenever the dialog opens or targets a different file.
  useEffect(() => {
    if (open) setValue(splitName(currentName).base);
  }, [open, currentName]);

  const handleSave = () => {
    if (isSaving) return;
    const trimmed = value.trim();
    if (!trimmed) {
      toast({
        title: 'שם הקובץ לא יכול להיות ריק',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
      return;
    }
    // The extension is never editable — always re-attach the original.
    onSave(`${trimmed}${ext}`);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent dir="rtl" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-right">שנה שם קובץ</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2" dir="ltr">
          <Input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              }
            }}
            placeholder="שם הקובץ"
            className="text-left flex-1"
            disabled={isSaving}
          />
          {ext && (
            <span className="text-sm text-muted-foreground shrink-0 select-none" dir="ltr">
              {ext}
            </span>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            ביטול
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
            שמור
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
